import { FilterInjection } from "app/filters/di/FilterInjection";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { findFeature } from "utils/format/FeatureFormat";
import { IFetchDataWithFeature } from "../repository/IFetchDataWithFeature";
import { IFilterRepository } from "../repository/IFilterRepository";

@injectable()
export class FilterUseCase {
  private filterRepo: IFilterRepository;

  constructor() {
    this.filterRepo = FilterInjection.getRepo();
  }

  async fetchListWithFeature<P extends Object, R extends Object>(
    props: IFetchDataWithFeature<P>,
  ): Promise<Array<R> | undefined> {
    const {
      path,
      savedKey,
      isForceRefresh,
      params,
      needExpired = true,
      isNeedFeature = true,
    } = props;

    if (isNeedFeature && !findFeature(path)) {
      if (savedKey) productCache.resetCache(savedKey);
      return undefined;
    } else {
      const cache = productCache.getCache(savedKey);

      if (cache && !isForceRefresh) {
        return cache;
      }

      const res = await this.filterRepo.getFilterList<P, R>(path, params);

      if (res?.data) {
        !!savedKey &&
          productCache.saveCache({
            key: savedKey,
            product: res.data,
            needExpired,
            timeExpired: 10800,
          });

        return res?.data;
      }
    }
  }

  async fetchDetailWithFeature<P extends Object, R extends Object>(
    props: IFetchDataWithFeature<P>,
  ) {
    const {
      path,
      savedKey,
      isForceRefresh,
      params,
      needExpired = true,
      isNeedFeature = true,
    } = props;
    if (isNeedFeature && !findFeature(path)) {
      if (savedKey) productCache.resetCache(savedKey);
      return undefined;
    } else {
      const cache = productCache.getCache(savedKey);

      if (cache && !isForceRefresh) {
        return cache;
      }

      const res = await this.filterRepo.getFilterDetail<P, R>(path, params);

      if (res?.data) {
        !!savedKey &&
          productCache.saveCache({
            key: savedKey,
            product: res.data,
            needExpired,
            timeExpired: 10800,
          });

        return res?.data;
      }
    }
  }

  async fetchListAndPagingWithFeature<P extends Object, R extends Object>(
    props: IFetchDataWithFeature<P>,
  ): Promise<{ data: R[]; pagination: any } | undefined> {
    const { path, params } = props;
    const res = await this.filterRepo.getFilterList<P, R>(path, params);
    if (res?.data) {
      return { data: res.data, pagination: res.pagination };
    }
    return undefined;
  }
}
